// First import on purpose: installs the crypto.randomUUID fallback for insecure
// contexts (plain-HTTP test hosts) before any shell or hosted-app code runs (#2201).
import "./randomUUIDFallback";
// Second on purpose: a remote support link (`#support=<id>.<secret>`) is a
// credential and must be out of the address before the router module reads it.
import "./supportLinkBoot";
// Third: a token the router staged for this tab becomes the session before
// anything mounts (a `?token=` over a different session, see auth.ts).
import "./pendingAdoptionBoot";
// Also before the router reads the address: the browser window's id belongs in it (I-3641).
import "./tiles/windowBoot";
import { createPinia } from "pinia";
import { createApp } from "vue";
import {
  connectSettingsCatalogKey,
  connectSettingsScopesKey,
} from "@blpde/connect";
import {
  NX_ROW_OPEN_BESIDE_KEY,
  NX_ROW_OPEN_TILES_KEY,
  NX_ROW_OPENER_KEY,
  nxSettingsKey,
} from "@blpde/nx-ui";
import { rowOpenTiles } from "./tiles/tileTargets";
import { createRecordOpener } from "./tiles/recordOpener";
import { useTilesEnabled } from "./tiles/tileNav";
import App from "./App.vue";
import { isAppEntitled } from "./applications";
import { initShellDesign } from "./design";
import i18n from "./i18n";
import { scopeGlobalTranslation } from "./i18n/appI18n";
import { registerErpDefinitionTexts } from "./integration/erpDefinitions";
import { installConnectApp, setConnectVueApp } from "./integration/loader";
import { installPageTitle } from "./pageTitle";
import router from "./router";
import { setCalendarInvitationCodecLoader } from "@blpde/connect";

setCalendarInvitationCodecLoader(
  async () =>
    (await import("@calendar/connect/invitations")).calendarInvitationCodec,
);
import { createShellSettingsCatalog } from "./settingsHub/settingsCatalog";
import { useShellSettingsScopes } from "./settingsHub/settingsScopes";
import { createAccountSettingsSource } from "./settingsSource";

// Seed the design store from storage and bind it to <html> before mount so the
// surface look is correct on first paint (mirrors the data-theme wiring).
initShellDesign();

const app = createApp(App);
// Hosted app modules install their plugins into this app when they load; the
// router's first navigation may already load one.
setConnectVueApp(app);
app.use(createPinia());
app.use(router);
app.use(i18n);
// Template `$t` & co. translate with the table of the app they render in (I-2284).
scopeGlobalTranslation(app);
// The texts of the ERP's screen definitions merge into the ERP's own table as they load.
registerErpDefinitionTexts();
// The browser tab says where the user stands (I-3777).
installPageTitle(router);
// The kit's settings (NxTable's table defaults, the panes of NxWorkspace, …)
// live on the account config path; hosted apps render inside this app and
// read the same source (I-2510).
app.provide(nxSettingsKey, createAccountSettingsSource());
// Where a record list opens a record: browser tab, beside, new workspace (I-3641).
app.provide(NX_ROW_OPENER_KEY, createRecordOpener(router));
app.provide(NX_ROW_OPEN_TILES_KEY, rowOpenTiles);
// Without tabs and tiles (`shell.tiles.enabled`) a row opens here or in a browser tab.
app.provide(NX_ROW_OPEN_BESIDE_KEY, useTilesEnabled());
// What every app declares for the one settings list (I-2510), collected once:
// the settings page, the look designer and the admin app's panels read it.
app.provide(
  connectSettingsCatalogKey,
  createShellSettingsCatalog(router, installConnectApp, isAppEntitled),
);
// Created in the consuming component's setup, where useI18n is available.
app.provide(connectSettingsScopesKey, () =>
  useShellSettingsScopes({
    router,
    install: installConnectApp,
    entitled: isAppEntitled,
    translate: (key, named) =>
      named ? i18n.global.t(key, named) : i18n.global.t(key),
  }),
);
app.mount("#app");
try {
  // The app runs: see `index.html`.
  sessionStorage.removeItem("connect.entryRetry");
} catch {
  // Without session storage there was no count either.
}
